import { useState } from 'react';

import { api } from '../../api';
import { go } from '../../App';
import { useLoad, usePolled } from '../../data';
import { InkHero, Loading, Panel, ago, fmtMoney, formatWhen, zoneDate } from '../../ui';
import { Account, Booking, Connection, Onboarding } from '../../types';

// The first-run state of Today (canon 11 E2 step 6): while a supplier is still
// onboarding, the home IS their setup checklist — the three E2 steps and the
// "Finish — I'm open for work" that opens them for work. Reuses the registered
// Business profile / My trips / Availability Forms (George's S11 ruling — no new
// screen), each reached one tap away, and finishes through POST /onboarding/complete.
const SETUP_STEPS: { key: 'basics' | 'product' | 'availability'; title: string; desc: string; to: string }[] = [
  { key: 'basics', title: 'Your business', desc: 'Name, what you do, where you leave from, languages.', to: '/profile' },
  { key: 'product', title: 'Your first trip', desc: 'Add a trip so hotels can book you.', to: '/trips' },
  { key: 'availability', title: 'When you work', desc: 'Your working days, hours and season.', to: '/availability' },
];

function OnboardingHome({ ob, reload }: { ob: Onboarding; reload: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const done = SETUP_STEPS.filter((s) => ob.steps[s.key]).length;

  const finish = async () => {
    setBusy(true);
    setError('');
    try {
      await api('POST', '/onboarding/complete');
      await reload();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <div>
      <InkHero eyebrow="Welcome" figure={`${done}/3`} unit="done" explain="A few steps and you're open for work." />

      <Panel title="Set up your business" meta={`${done} of 3`} flush>
        <table>
          <tbody>
            {SETUP_STEPS.map((s) => {
              const isDone = ob.steps[s.key];
              return (
                <tr key={s.key} className="click" onClick={() => go(s.to)}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <span className={`uchip ${isDone ? 'waiting' : 'soon'}`}>{isDone ? 'Done' : 'To do'}</span>
                  </td>
                  <td>
                    {s.title}
                    <br />
                    <span className="hint">{s.desc}</span>
                  </td>
                  <td className="num"><span className="open">{isDone ? 'Edit →' : 'Do this →'}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Panel>

      {error && <p className="error-line" role="alert">{error}</p>}

      <div className="form-foot">
        <span className="hint" style={{ flex: 1, alignSelf: 'center' }}>
          {ob.complete ? "Everything's set — open yourself for work." : 'Finish the steps above first.'}
        </span>
        <button className="btn" onClick={finish} disabled={busy || !ob.complete} style={{ flex: 'none', minWidth: 200 }}>
          {busy ? 'Opening…' : "Finish — I'm open for work"}
        </button>
      </div>
    </div>
  );
}

// The supplier home. The hero owns today's single most important fact: the next
// job, or the good news that nothing needs them. Beneath it is **Eva's task
// list** (§4.3) — the cross-module "what goes wrong soonest" queue, built as a
// Today section (canon 09's register is closed at 41; "Eva tasks" is prose, not
// a 42nd screen). It folds the reads Today already polls — waiting requests and
// connection requests — into one prioritised list, each row carrying its
// existing one-tap target. A deterministic fold (the same discipline as the
// rest of Eva's core); a model-written ordering is the faithful upgrade.

// One actionable item across modules. `accept` rows carry the connection so the
// row can approve it inline through the SAME POST the My hotels screen uses.
interface Task {
  key: string;
  severity: number; // lower = more urgent
  chipClass: 'escalated' | 'soon' | 'waiting';
  chipText: string;
  title: string;
  meta: string;
  go?: () => void;
  connection?: Connection;
}

export function Today() {
  const { data, error } = usePolled<{ items: Booking[] }>('/bookings');
  const connections = usePolled<{ items: Connection[] }>('/connections');
  const account = useLoad<Account>('/account');
  const onboarding = useLoad<Onboarding>('/onboarding');
  const items = data ? data.items : null;
  const pendingConns = (connections.data?.items ?? []).filter((c) => c.status === 'pending');
  // The accepted connection's card vanishes with the reload — this line is
  // the toast-shaped acknowledgement that names what changed.
  const [connectedName, setConnectedName] = useState('');

  // "Today" in the property's day — never the device's, never UTC. At
  // 01:00 Athens, a 09:00 job is today, whatever UTC says.
  const today = zoneDate(account.data?.timezone ?? 'Europe/Athens', Date.now());
  const jobsToday = (items ?? [])
    .filter((b) => b.status === 'confirmed' && b.start.slice(0, 10) === today)
    .sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
  const waiting = (items ?? []).filter((b) => b.status === 'pending' || b.status === 'requested');
  const upcoming = (items ?? [])
    .filter((b) => b.status === 'confirmed' && b.start.slice(0, 10) > today)
    .sort((a, b) => Date.parse(a.start) - Date.parse(b.start))
    .slice(0, 5);

  // The fold: every pending item, most-urgent first (canon 11 F3's spirit —
  // escalated, then starting soon, then oldest waiting; connection asks sit
  // just under the escalated line, above ordinary waits).
  const tasks: Task[] = [];
  const soonMs = Date.now() + 24 * 3600_000;
  for (const b of waiting) {
    const esc = b.chase?.state === 'escalated';
    const starts = Date.parse(b.start);
    const soon = starts > Date.now() && starts < soonMs;
    tasks.push({
      key: `req-${b.id}`,
      severity: esc ? 0 : soon ? 1 : 3,
      chipClass: esc ? 'escalated' : soon ? 'soon' : 'waiting',
      chipText: esc ? 'Answer now' : soon ? `Starts ${formatWhen(b.start)}` : `Waiting ${ago(b.createdAt)}`,
      title: b.productName ?? b.productId,
      meta: `${b.hotelName ?? 'a hotel'} · ${b.guests.adults + (b.guests.children ?? 0)} guests · ${fmtMoney(b.supplierAmount)}`,
      go: () => go(`/requests/${b.id}`),
    });
  }
  for (const c of pendingConns) {
    tasks.push({
      key: `conn-${c.hotelAccountId ?? c.accountId}`,
      severity: 2,
      chipClass: 'soon',
      chipText: 'Wants to connect',
      title: c.name ?? c.hotelName ?? 'A hotel',
      meta: 'A hotel wants to send you guests — connecting lets them book you.',
      connection: c,
    });
  }
  tasks.sort((a, b) => a.severity - b.severity);

  // A supplier still setting up gets the first-run onboarding home (canon 11 E2
  // step 6), not the empty task list — the checklist that opens them for work.
  if (onboarding.data?.status === 'onboarding') {
    return <OnboardingHome ob={onboarding.data} reload={onboarding.reload} />;
  }

  return (
    <div>
      <InkHero
        eyebrow="Today"
        figure={items === null ? '…' : jobsToday.length}
        unit={jobsToday.length === 1 ? 'job' : 'jobs'}
        explain={
          jobsToday[0]
            ? `Next: ${jobsToday[0].productName} at ${jobsToday[0].start.slice(11, 16)} · ${jobsToday[0].guests.adults + (jobsToday[0].guests.children ?? 0)} guests`
            : items === null
              ? ' '
              : 'No departures today.'
        }
      />

      {connectedName && (
        <Panel style={{ marginBottom: 16 }}>
          <span style={{ color: 'var(--color-success)' }}>
            Connected — <strong>{connectedName}</strong> can book you now.
          </span>
        </Panel>
      )}

      {items === null ? (
        error ? <p className="error-line">{error}</p> : <Loading />
      ) : tasks.length === 0 ? (
        <div className="empty">
          <strong>Nothing needs you</strong>
          <p>This is the good outcome, not an empty screen. Requests and connection asks land here, most urgent first.</p>
        </div>
      ) : (
        <Panel title="Your list" meta={`${tasks.length} to do`} flush>
          <table>
            <tbody>
              {tasks.map((t) => (
                <TaskRow
                  key={t.key}
                  task={t}
                  onConnected={async (name) => {
                    setConnectedName(name);
                    await connections.reload();
                  }}
                />
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {upcoming.length > 0 && (
        <Panel title="Coming up" style={{ marginTop: 20 }} flush>
          <table>
            <tbody>
              {upcoming.map((b) => (
                <tr key={b.id}>
                  <td className="mono">{formatWhen(b.start)}</td>
                  <td>
                    {b.productName}
                    <br />
                    <span className="hint">
                      {b.hotelName} · {b.guests.adults + (b.guests.children ?? 0)} guests
                    </span>
                  </td>
                  <td className="num money">{fmtMoney(b.supplierAmount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
}

function TaskRow({ task, onConnected }: { task: Task; onConnected: (name: string) => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const accept = async () => {
    if (!task.connection) return;
    const hotelId = task.connection.hotelAccountId ?? task.connection.accountId ?? '';
    const name = task.connection.name ?? task.connection.hotelName ?? 'The hotel';
    setBusy(true);
    setError('');
    try {
      await api('POST', `/connections/${hotelId}/accept`);
      await onConnected(name);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <tr className={task.go ? 'click' : ''} onClick={task.go}>
      <td style={{ whiteSpace: 'nowrap' }}>
        <span className={`uchip ${task.chipClass}`}>{task.chipText}</span>
      </td>
      <td>
        {task.title}
        <br />
        <span className="hint">{task.meta}</span>
        {error && <p className="error-line" role="alert">{error}</p>}
      </td>
      <td className="num">
        {task.connection ? (
          <button className="btn" disabled={busy} onClick={accept}>
            {busy ? 'Connecting…' : 'Connect'}
          </button>
        ) : (
          <span className="open">Answer →</span>
        )}
      </td>
    </tr>
  );
}
